// CoordiDetailPage.tsx
import React, {useEffect, useRef, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {getCoordinatorDetail, getCoordinatorReviews, getJobPostingDetail} from '../../api/jobApi';
import {findAreaList, getAreaList} from '../../api/commonApi';
import {maskName, maskPhone, setComma} from "../../utils/format";
import {Loading} from '../../components/common/Loading';
import {EmptyState} from "../../components/common/EmptyStateProps";
import defaultMaleProfileImg from "../../assets/images/man_default_img.png";
import defaultFemaleProfileImg from "../../assets/images/woman_default_img.png";
import {toggleFavorite} from "../../api/coordiApi";


export default function CoordiDetailPage() {
    const navigate = useNavigate();
    const {jobPostingCode, coordinatorCode} = useParams<{ jobPostingCode: string; coordinatorCode: string }>();
    const [activeTab, setActiveTab] = useState('tab-1');
    const [isFavorite, setIsFavorite] = useState(false);
    const [data, setData] = useState<any>(null);
    const [reviews, setReviews] = useState<any[]>([]);
    const [areaDisplay, setAreaDisplay] = useState<string>('');
    const [workType, setWorkType] = useState<string>('');
    const [jobStatus, setJobStatus] = useState<string>('');
    const [loading, setLoading] = useState(true);
    const [matchedCoordinatorCode, setMatchedCoordinatorCode] = useState<string | null>(null);
    const [showPhoneAction, setShowPhoneAction] = useState(false);

    useEffect(() => {
        if (coordinatorCode) {
            fetchCoordinatorDetail();
            fetchReviews();
            // 공고 정보에서 workType 가져오기
            if (jobPostingCode) {
                fetchJobPostingInfo();
            }
        }
    }, [coordinatorCode, jobPostingCode]);

    const fetchJobPostingInfo = async () => {
        try {
            const response = await getJobPostingDetail(jobPostingCode || '');
            // ResponseDto 구조: response.data = { code, data: {...} }
            const jobData = response.data?.data || response.data;
            if (jobData?.workType) {
                setWorkType(jobData.workType);
            }
            if (jobData?.status) {
                setJobStatus(jobData.status);
            }
            // sessionStorage 대신 API 응답에서 직접 읽도록 변경
            const coordinatorCodeFromApi = jobData?.coordinatorCode ?? null;
            setMatchedCoordinatorCode(coordinatorCodeFromApi);
        } catch (error) {
            console.error('Failed to fetch job posting info:', error);
        }
    };

    const fetchCoordinatorDetail = async () => {
        try {
            if (!coordinatorCode) {
                console.error('Coordinator code is required');
                setLoading(false);
                return;
            }
            // jobPostingCode가 없으면 'none' 사용
            const jobCode = jobPostingCode || 'none';
            const response = await getCoordinatorDetail(coordinatorCode, jobCode);

            // response.data가 바로 CoordinatorData
            const detail = response.data;

            if (!detail) {
                console.error('No coordinator data received');
                setLoading(false);
                return;
            }

            setData(detail);
            setIsFavorite(detail.favorite || false);
            // 지역 코드 → 지역명 변환
            await fetchAreaDisplay(detail?.preferredAreaMajor, detail?.preferredAreaMinor);
        } catch (error) {
            console.error('Failed to fetch coordinator detail:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchAreaDisplay = async (majorCode: string, minorCode: string) => {
        try {
            let majorName = '';
            let minorName = '';
            if (majorCode) {
                const majorRes = await getAreaList();
                const majorArea = majorRes.data?.data?.find((a: any) => a.coCode === majorCode);
                majorName = majorArea?.coName || majorCode;
            }
            if (majorCode && minorCode) {
                const minorRes = await findAreaList(majorCode);
                const minorArea = minorRes.data?.data?.find((a: any) => a.coCode === minorCode);
                minorName = minorArea?.coName || minorCode;
            }
            setAreaDisplay([majorName, minorName].filter(Boolean).join(' '));
        } catch (error) {
            console.error('Failed to fetch area names:', error);
            setAreaDisplay([majorCode, minorCode].filter(Boolean).join(' '));
        }
    };

    const fetchReviews = async () => {
        try {
            const response = await getCoordinatorReviews(coordinatorCode || '');
            setReviews(response.data?.data || []);
        } catch (error) {
            console.error('Failed to fetch reviews:', error);
        }
    };

    const StarRating = ({rating = 0}) => (
        <span className="inline-flex gap-0.5">
            {[1, 2, 3, 4, 5].map((star) => (
                <span key={star} className={star <= rating ? 'text-yellow-400' : 'text-gray-300'}>★</span>
            ))}
        </span>
    );

    const ReviewBox = ({review}: { review: any }) => {
        const [isExpanded, setIsExpanded] = useState(false);
        const [needsButton, setNeedsButton] = useState(false);
        const textRef = useRef(null);

        useEffect(() => {
            const checkHeight = () => {
                const element = textRef.current;
                if (!element) return;
                try {
                    const styles = window.getComputedStyle(element);
                    const lineHeight = parseFloat(styles.lineHeight) || 24;
                    const clampHeight = lineHeight * 3;
                } catch (error) {
                }
            };
            const timer = setTimeout(checkHeight, 150);
            return () => clearTimeout(timer);
        }, [review.content]);

        return (
            <div className="review-box">
                <div className="grade-info">
                    <span className="grade-ico">
                        <StarRating rating={review.rating}/>
                    </span>
                    <span className="date">{review.regDate}</span>
                    <span className="id">{review.regUser}</span>
                </div>
                <div
                    ref={textRef}
                    className="review-txt"
                    style={{
                        maxHeight: isExpanded ? '1000px' : '4.5em',
                        overflow: 'hidden',
                        transition: 'max-height 0.3s'
                    }}
                >
                    {review.content}
                </div>
                {needsButton && (
                    <button type="button" className="btn-more" onClick={() => setIsExpanded(!isExpanded)}>
                        {isExpanded ? '접기' : '더보기'}
                    </button>
                )}
            </div>
        );
    };

    const handlePaymentClick = () => {
        if (!isRecruiting) {
            alert('모집중인 공고만 코디를 선택할 수 있습니다.');
            return;
        }
        navigate(`/payment?jobPostingCode=${jobPostingCode}&coordinatorCode=${coordinatorCode}`);
    };

    const isMatchedCoordinator = matchedCoordinatorCode === coordinatorCode;
    const isRecruiting = jobStatus === 'RECRUITING';
    // 결제완료(MATCHED) 이후 상태면 마스킹 해제
    const isPaid = !!jobPostingCode && ['MATCHED', 'ONGOING', 'COMPLETED'].includes(jobStatus);
    const shouldMask = !isPaid;

    if (!data) {
        return <EmptyState message="코디네이터 정보를 찾을 수 없습니다."/>;
    }

    const handleToggleFavorite = async (coordinatorCode: string) => {
        try {
            const response = await toggleFavorite(coordinatorCode);
            setIsFavorite(response.data);
        } catch (error) {
            console.error('Failed to toggle favorite:', error);
        }
    };

    return (
        <>
            <Loading loading={loading} onClose={() => setLoading(false)}/>
            <div>
                {!isMatchedCoordinator && jobPostingCode && isRecruiting && (
                    <div className="bottom-btn">
                        <button type="button" className="btn confirm" onClick={handlePaymentClick}>
                            코디 선택
                        </button>
                    </div>
                )}

                <main className="container type_gray">
                    <section className="bg-white">
                        <div className="inner-box">
                            <div className="coordi-profile-box top">
                                <div className="profile-img">
                                    <div className="thumbnail-wrap">
                                        <div className="thumbnail">
                                            <div className="centered">
                                                <img
                                                    src={data.imagePath && data.imagePath !== 'default' ? data.imagePath : (data.gender === '여성' ? defaultFemaleProfileImg : defaultMaleProfileImg)}
                                                    className="portrait"
                                                    alt="프로필"
                                                    onError={(e) => {
                                                        (e.target as HTMLImageElement).src = data.gender === '여성' ? defaultFemaleProfileImg : defaultMaleProfileImg;
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="coordi-info">
                                    <p className="name">
                                        {data.unmaskedName || data.memberName}
                                        <span className="career">
                                            {data.hasCareer
                                                ? `경력 ${[
                                                    Number(data.careerYear) > 0 ? `${data.careerYear}년` : '',
                                                    Number(data.careerMonth) > 0 ? `${data.careerMonth}개월` : ''
                                                ].filter(Boolean).join(' ')}`
                                                : '미경력'}
                                        </span>
                                    </p>
                                    <p className="intro">{data.introduction || '안녕하세요. 가족처럼 성심을 다해 도움을 드리겠습니다.'}</p>
                                </div>
                                <span className="like-check">
                                    <input type="checkbox" id="favorites1" className="blind"
                                           checked={isFavorite || false}
                                           onChange={() => handleToggleFavorite(data.coordinatorCode)}/>
                                    <label htmlFor="favorites1"></label>
                                </span>
                            </div>
                        </div>
                    </section>

                    <section className="no-gap">
                        <div className="normal-tabs-wrap">
                            <ul className="normal-tabs">
                                <li className={`tab-link ${activeTab === 'tab-1' ? 'current' : ''}`}
                                    onClick={() => setActiveTab('tab-1')}>소개
                                </li>
                                <li className={`tab-link ${activeTab === 'tab-2' ? 'current' : ''}`}
                                    onClick={() => setActiveTab('tab-2')}>후기 ({reviews.length})
                                </li>
                            </ul>
                        </div>

                        <div id="tab-1" className={`normal-tab-content ${activeTab === 'tab-1' ? 'current' : ''}`}>
                            <div className="bg-white">
                                <div className="coordi-cate">
                                    <div className="ico-info">
                                        <p className="tit">나이</p>
                                        <p className="cont"><i className="fa-regular fa-file-lines"></i>{data.age}</p>
                                    </div>
                                    <div className="ico-info line">
                                        <p className="tit">성별</p>
                                        <p className="cont">
                                            <i className={`fa-solid ${data.gender === '남성' ? 'fa-mars' : 'fa-venus'}`}></i>
                                            {data.gender}
                                        </p>
                                    </div>
                                    <div className="ico-info line">
                                        <p className="tit">국적</p>
                                        <p className="cont">{data.nationalityName}</p>
                                    </div>
                                </div>
                            </div>

                            {jobPostingCode && (
                                <div className="bg-white no-gap">
                                    <div className="inner-box">
                                        <div className="round-box orange">
                                            <div className="round-body">
                                                <div className="info-row">
                                                    <span
                                                        className="label">{workType === 'HOURLY' ? '요청 시급' : '요청 일급'}</span>
                                                    <span className="cont big-number">{setComma(data.amount)}원</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="bg-white">
                                <div className="inner-box">
                                    <h3 className="cont-tit"><span className="underline">코디 정보</span></h3>
                                    <div className="round-box mt10">
                                        <div className="round-body">
                                            <div className="info-row">
                                                <span className="label">코디번호</span>
                                                <span className="cont">{data.coordinatorCode}</span>
                                            </div>
                                            <div className="info-row">
                                                <span className="label">이름</span>
                                                <span
                                                    className="cont">{data.unmaskedName || data.memberName}</span>
                                            </div>
                                            <div className="info-row">
                                                <span className="label">지역</span>
                                                <span
                                                    className="cont">{areaDisplay || '-'}</span>
                                            </div>
                                            <div className="info-row">
                                                <span className="label">돌봄 건수</span>
                                                <span className="cont">{data.completed || 0}건</span>
                                            </div>
                                            <div className="info-row">
                                                <span className="label">연락처</span>
                                                <span className="cont">
                                                    {shouldMask ? maskPhone(data.phone) : (
                                                        data.phone ? (
                                                            <span
                                                                style={{
                                                                    color: '#f6973f',
                                                                    textDecoration: 'underline',
                                                                    cursor: 'pointer'
                                                                }}
                                                                onClick={() => setShowPhoneAction(true)}
                                                            >
                                                                {data.phone}
                                                            </span>
                                                        ) : '***********'
                                                    )}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {data.certificates && data.certificates.length > 0 && (
                                <div className="bg-white">
                                    <div className="inner-box">
                                        <h3 className="cont-tit"><span className="underline">자격증 정보</span></h3>
                                        <div className="round-box mt10">
                                            <div className="round-body">
                                                {data.certificates.map((cert: any, index: number) => (
                                                    <div className="info-row" key={index}>
                                                        <span className="label">{cert.certificateName}</span>
                                                        <span className="cont date">{cert.certificateIssuer}
                                                            <span className="date">({cert.issueDate})</span>
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div id="tab-2"
                             className={`normal-tab-content bg-white ${activeTab === 'tab-2' ? 'current' : ''}`}>
                            <div className="inner-box">
                                {reviews.length === 0 ? (
                                    <p className="empty-txt">등록된 후기가 없습니다.</p>
                                ) : (
                                    reviews.map((review: any) => (
                                        <ReviewBox key={review.reviewCode} review={review}/>
                                    ))
                                )}
                            </div>
                        </div>
                    </section>
                </main>

                {/* 전화/문자 액션시트 */}
                {showPhoneAction && data?.phone && (
                    <div className="action-sheet-overlay" onClick={() => setShowPhoneAction(false)}>
                        <div className="action-sheet" onClick={e => e.stopPropagation()}>
                            <button
                                type="button"
                                className="action-sheet-item"
                                onClick={() => {
                                    window.location.href = `tel:${data.phone}`;
                                    setShowPhoneAction(false);
                                }}
                            >
                                전화걸기
                            </button>
                            <button
                                type="button"
                                className="action-sheet-item"
                                onClick={() => {
                                    window.location.href = `sms:${data.phone}`;
                                    setShowPhoneAction(false);
                                }}
                            >
                                문자보내기
                            </button>
                            <button
                                type="button"
                                className="action-sheet-cancel"
                                onClick={() => setShowPhoneAction(false)}
                            >
                                취소
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
}